import type { SentryFlow } from './sentry-flow';

/**
 * How a query/mutation failure is reported. Shared by `Register['queryMeta']` and
 * `Register['mutationMeta']` (apps/hub/src/global.d.ts) so the two cannot drift.
 *
 * A union, not two optional fields: `flow` tags the capture `handleGlobalError` makes, and a
 * 'self-reported' caller never reaches that capture — so the dead combination is unrepresentable
 * rather than merely warned about.
 */
export type ErrorHandlingMeta = CentrallyReportedMeta | SelfReportedMeta;

/** Reaches the central capture in `handleGlobalError`; the two cases differ only in the user-facing reaction. */
type CentrallyReportedMeta = {
  /**
   * Omitted → generic toast / forbidden-redirect AND the central Sentry capture.
   * 'silent' → caller owns the reaction (or the failure is non-critical enrichment), so the
   * toast/redirect is suppressed — but an unexpected failure still must not go unnoticed, so the
   * capture stays.
   */
  errorHandling?: 'silent';
  /**
   * Sentry `flow` tag for that capture. Tagging here rather than capturing again at the call site
   * keeps one enriched event per failure: a second capture either duplicates the event (ZodError,
   * fingerprinted on the scope) or loses to Dedupe (5xx, fingerprinted later in `beforeSend`).
   *
   * Not the hey-api transport meta (`{ recaptcha }`, `{ stepUp: true }`), which is an argument to
   * the generated endpoint and never reaches `handleGlobalError`.
   */
  flow?: SentryFlow;
};

/**
 * Caller both reacts and reports: it classifies known codes and escalates the unmapped case via
 * `extractApiErrorCode`, so toast, redirect and central capture are all suppressed.
 *
 * No `flow`: there is no central capture to tag — the flow goes to `extractApiErrorCode` instead.
 */
type SelfReportedMeta = {
  errorHandling: 'self-reported';
  flow?: never;
};

/** See {@link CentrallyReportedMeta}. */
export const SILENT_ERROR_META = { errorHandling: 'silent' } as const satisfies ErrorHandlingMeta;

/** See {@link SelfReportedMeta}. */
export const SELF_REPORTED_ERROR_META = { errorHandling: 'self-reported' } as const satisfies ErrorHandlingMeta;
